import React, { useCallback } from 'react';
import { useAuthStore } from '@store/authStore';
import { useAppStore } from '@store/appStore';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api';
import { RiInboxLine, RiFileTextLine, RiPulseFill, RiPlaneLine, RiCheckLine, RiSendPlaneLine, RiBookmarkLine, RiStarLine, RiBuildingLine, RiMapPinLine } from '@remixicon/react';
import { Card, CardContent } from '@components/ui/Card';
import { MessagesInbox } from '@/features/messages';
import { Skeleton } from '@components/ui/Skeleton';
/**
 * Dashboard Page
 * Main dashboard with bids inbox, stats, recent activity, and shortlist
 */
const Dashboard = () => {
  const queryClient = useQueryClient();
  const { userProfile } = useAuthStore();
  const { updateBidOption } = useAppStore();
  const profileId = userProfile?.userprofile_id;

  // Mocked recent activity data
  const recentActivities = [
    /*{
      id: 1,
      type: 'bid_received',
      icon: <RiInboxLine className="w-4 h-4 text-blue-600" />,
      iconBg: 'bg-blue-100',
      title: 'New Bid Received',
      description: 'Pinnacle Air Services quoted $17,400 for TEB → MIA',
      tags: [
        { icon: <RiPlaneLine className="w-4 h-4" />, label: 'N456LX' },
        { icon: <RiBuildingLine className="w-4 h-4" />, label: 'Pinnacle Air Services' },
        { icon: <RiMapPinLine className="w-4 h-4" />, label: 'TEB → MIA' },
      ],
      time: '12h',
    },
    {
      id: 2,
      type: 'bid_received',
      icon: <RiInboxLine className="w-4 h-4 text-blue-600" />,
      iconBg: 'bg-blue-100',
      title: 'New Bid Received',
      description: 'Skyward Aviation quoted $6,200 for VNY → LAS',
      tags: [
        { icon: <RiPlaneLine className="w-4 h-4" />, label: 'N789HP' },
        { icon: <RiBuildingLine className="w-4 h-4" />, label: 'Skyward Aviation' },
        { icon: <RiMapPinLine className="w-4 h-4" />, label: 'VNY → LAS' },
      ],
      time: '1d',
    },
    {
      id: 3,
      type: 'booking_confirmed',
      icon: <RiCheckLine className="w-4 h-4 text-green-600" />,
      iconBg: 'bg-green-100',
      title: 'Booking Confirmed',
      description: 'JFK → LAX flight confirmed with Pinnacle Air Services',
      tags: [
        { icon: <RiPlaneLine className="w-4 h-4" />, label: 'N890VT' },
        { icon: <RiBuildingLine className="w-4 h-4" />, label: 'Pinnacle Air Services' },
        { icon: <RiMapPinLine className="w-4 h-4" />, label: 'JFK → LAX' },
      ],
      time: '1d',
    },
    {
      id: 4,
      type: 'bid_request_sent',
      icon: <RiSendPlaneLine className="w-4 h-4 text-purple-500" />,
      iconBg: 'bg-purple-100',
      title: 'Bid Request Sent',
      description: 'RFP sent to 4 operators for DAL → HOU',
      tags: [
        { icon: <RiMapPinLine className="w-4 h-4" />, label: 'DAL → HOU' },
      ],
      time: '2d',
    },
    {
      id: 5,
      type: 'search_saved',
      icon: <RiBookmarkLine className="w-4 h-4 text-orange-500" />,
      iconBg: 'bg-orange-100',
      title: 'Search Saved',
      description: 'Saved search for Light Jets, 4-6 passengers',
      tags: [],
      time: '3d',
    },
    {
      id: 6,
      type: 'profile_reviewed',
      icon: <RiStarLine className="w-4 h-4 text-amber-500" />,
      iconBg: 'bg-amber-50',
      title: 'Operator Profile Reviewed',
      description: 'Reviewed Skyward Aviation (TrustScore: 94)',
      tags: [
        { icon: <RiBuildingLine className="w-4 h-4" />, label: 'Skyward Aviation' },
      ],
      time: '3d',
    },*/
  ];

  // Fetch quotes for dashboard stats
  const { data: quotes = [], isLoading: quotesLoading } = useQuery({
    queryKey: ['quotes', 'dashboard', profileId],
    queryFn: async () => {
      const data = await apiClient.getQuotes(0, 100, { created_by: profileId });
      return data.filter(quote =>
        quote.bid_items?.some(bidItem =>
          bidItem.bid?.bid_options?.some(option =>
            option.shortlisted == true || option.shortlisted === 1
          )
        )
      ).map(quote => ({
        ...quote,
        id: quote.quote_id,
        status: quote.status || 'DRAFT'
      }));
    },
    enabled: !!profileId,
    staleTime: 0,
    refetchOnMount: 'always',
    retry: 2,
    retryDelay: 1000,
  });


  /**
   * React Query mutation for shortlisting bid options
   * Automatically invalidates and refetches the bidOptions cache
   */
  const shortlistMutation = useMutation({
    mutationFn: async (bidOptionData) => {
      const newShortlistedStatus = !bidOptionData.shortlisted;
      return await apiClient.updateBidOption(bidOptionData.bid_option_id, {
        ...bidOptionData,
        shortlisted: newShortlistedStatus
      });
    },
    onSuccess: (data, bidOptionData) => {
      const newShortlistedStatus = !bidOptionData.shortlisted;
      // Optimistically update Zustand store
      updateBidOption(bidOptionData.bid_option_id, { shortlisted: newShortlistedStatus });
      // Invalidate and refetch both bid options and shortlisted bids
      queryClient.invalidateQueries({ queryKey: ['bidOptions'] });
      queryClient.invalidateQueries({ queryKey: ['shortlistedBids'] });
    },
    onError: (err) => {
      console.error('Error updating bid option:', err);
    }
  });

  const shortListBidOption = useCallback(async (bidOptionData) => {
    await shortlistMutation.mutateAsync(bidOptionData);
  }, [shortlistMutation]);

  /**
   * Calculate stats from quotes
   */
  const stats = {
    totalQuotes: quotes.length,
    accepted: quotes.filter(q => q.status === 'ACCEPTED').length,
    sent: quotes.filter(q => q.status === 'SENT').length,
    draft: quotes.filter(q => q.status === 'DRAFT').length,
  };

  return (
    <div>
      <div className="max-w-full">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

          {/* Left Column - Bids Inbox 
          <div className="flex flex-col min-h-[calc(100vh-240px)]">
            <MessagesInbox messages={allBidOptions} loading={bidsLoading} hanldeShortList={shortListBidOption} />
          </div>*/}

          {/* Right Column - Stats + Recent Activity */}
          <div className="flex flex-col gap-6 min-h-[calc(100vh-240px)]">
            {/* Stats Cards */}
            <div className="grid grid-cols-2 gap-4">

              <Card className="overflow-hidden bg-white !rounded-2xl shadow-sm border !border-gray-100">
                <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="text-5xl font-normal text-gray-900">
                          {quotesLoading ? (
                              <div className="space-y-3">
                                <Skeleton className="h-4 w-16" />
                              </div>
                            ) : stats.totalQuotes}
                        </div>
                        <p className="text-sm text-gray-500 mt-1">Total Bids</p>
                      </div>
                      <div className="p-2 bg-blue-100 rounded-full">
                        <RiInboxLine className="w-5 h-5 text-blue-600" />
                      </div>
                    </div>
                </CardContent>
              </Card>

              <Card className="overflow-hidden bg-white !rounded-2xl shadow-sm border !border-gray-100">
                <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="text-5xl font-normal text-gray-900">
                          {quotesLoading ? (
                            <div className="space-y-3">
                              <Skeleton className="h-4 w-16" />
                            </div>
                          ) : stats.accepted}
                        </div>
                        <p className="text-sm text-gray-500 mt-1">Accepted</p>
                      </div>
                      <div className="p-2 bg-green-100 rounded-full">
                        <RiFileTextLine className="w-5 h-5 text-green-600" />
                      </div>
                    </div>
                </CardContent>
              </Card>

            </div>

            {/* Recent Activity */}
            <div className="bg-white !rounded-2xl shadow-sm border border-gray-100 p-6 flex-1">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-2">
                  <RiPulseFill className="w-5 h-5 text-amber-500" />
                  <h2 className="text-lg font-semibold text-gray-900">Recent Activity</h2>
                </div>
                <button className="px-4 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-2xl hover:bg-gray-50 transition-colors">
                  See All
                </button>
              </div>

              <div className="space-y-4">
                {recentActivities.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-gray-500 text-sm">No recent activities</p>
                  </div>
                ) : (
                  recentActivities.map((activity, index) => (
                    <div key={activity.id} className="flex items-start gap-3 relative">
                      {/* Vertical line connecting icons */}
                      {index < recentActivities.length - 1 && (
                        <div className="absolute left-[18px] top-[35px] bottom-[-8px] w-[2px] bg-gray-100" />
                      )}
                      <div className={`p-2 rounded-2xl ${activity.iconBg} flex-shrink-0 relative z-10`}>
                        {activity.icon}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-medium text-gray-900 text-sm">{activity.title}</h3>
                        <p className="text-sm text-gray-500 mt-0.5">{activity.description}</p>
                        {activity.tags.length > 0 && (
                          <div className="flex flex-wrap gap-2 mt-2">
                            {activity.tags.map((tag, index) => (
                              <span
                                key={index}
                                className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-md"
                              >
                                <span className="text-xs">{tag.icon}</span>
                                {tag.label}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                      <span className="text-xs text-gray-400 flex-shrink-0">{activity.time}</span>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
